import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { Route, RouteRecord } from 'vue-router'
import type { Store } from 'vuex'
import type {
  AppRoute,
  ContextState,
  FeatureNode,
  FeaturesTree,
  GuardResolveFunction,
} from '../models'
import type { ContextProvider } from '../providers'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import { PermissionLevelEnum } from '@empathybroker/tsclient-play/dist/enums/permission-level.enum'

import { CONTAINER } from '../config'
import { TYPES } from '../config/inversify.types'

import { FeatureIdEnum } from '../enums'

import { GuardClass } from '../models'

import { ROUTER_CONSTS } from '../router/router.consts'
import { CONFIG_CONSTS } from '../store/config/config.consts'

import { FeaturesTreeUtils, FiltersUtils } from '../utils'

export class AuthorizeRouteGuard extends GuardClass {
  static async execute(
    this: void,
    to: AppRoute,
    from: Route,
    next: GuardResolveFunction,
  ): Promise<void> {
    const instanceConfig = await AuthorizeRouteGuard.getInstanceConfig(to)
    if (instanceConfig) {
      const featuresTree = FeaturesTreeUtils.getFeaturesTree(instanceConfig)

      if (!AuthorizeRouteGuard.isRouteAccesible(to, featuresTree)) {
        next({
          ...AuthorizeRouteGuard.getDefaultRoute(to, featuresTree),
          replace: true,
        })
        return
      }
    }

    next()
  }

  static updateRouteWithContext(route: Route, context: ContextState): Route {
    const updatedQuery = {
      ...route.query,
    }

    if (context.query) {
      updatedQuery[ROUTER_CONSTS.query.query] = context.query
    }
    if (context.strictMatching) {
      updatedQuery[ROUTER_CONSTS.query.strictMatching] = context.strictMatching.toString()
    }

    let updatedRoute: AppRoute = {
      ...route,
      params: {
        ...route.params,
        [ROUTER_CONSTS.params.instanceId]: context.instanceId,
      },
      query: updatedQuery,
    } as AppRoute

    updatedRoute = FiltersUtils.updateRouteFiltersWithContext(updatedRoute, context)

    return updatedRoute
  }

  // TODO: Move this somewhere else (eg: a getter) and use it here an in SetContextWithUrlGuard
  private static async getInstanceConfig(route: Route): Promise<Instance | undefined> {
    const store = CONTAINER.get<Store<any>>(TYPES.Store)
    const instances: Instance[] =
      await store.getters[`${CONFIG_CONSTS.moduleName}/${CONFIG_CONSTS.getters.instances}`]

    let instanceConfig: Instance | undefined
    const context = this.getContext()
    const instanceId = context.instanceId || route.params[ROUTER_CONSTS.params.instanceId]
    if (instanceId) {
      instanceConfig = instances.find((instance: Instance) => instance.id === instanceId)
    }
    if (!instanceConfig && instances.length === 1) {
      instanceConfig = instances[0]
    }
    return instanceConfig
  }

  private static isRouteAccesible(route: AppRoute, featuresTree: FeaturesTree): boolean {
    let domain: DomainEnum | undefined
    route.matched.some((route: RouteRecord) => {
      if (route.meta) {
        domain = route.meta.domain as DomainEnum
      }
      return !!domain
    })
    domain = domain || DomainEnum.GLOBAL

    const domainFeatureNodes = featuresTree[domain] || []
    const featureNode = FeaturesTreeUtils.getFeatureNode(
      route.meta?.featureId ?? '',
      domainFeatureNodes,
    )
    return !!featureNode && featureNode.permissionLevel !== PermissionLevelEnum.NONE
  }

  private static getDefaultRoute(route: Route, featuresTree: FeaturesTree): Route {
    let defaultRouteName: string = ''
    const highlightsNode = featuresTree[DomainEnum.GLOBAL]?.find(
      (featureNode: FeatureNode) => featureNode.id === FeatureIdEnum.HIGHLIGHTS,
    )
    if (!!highlightsNode && highlightsNode.permissionLevel !== PermissionLevelEnum.NONE) {
      defaultRouteName = highlightsNode.routeName ?? ''
    } else {
      Object.keys(featuresTree).some(domain => {
        defaultRouteName = AuthorizeRouteGuard.getDefaultRouteName(featuresTree[domain]) ?? ''
        return !!defaultRouteName
      })
    }
    return {
      ...route,
      name: defaultRouteName || ROUTER_CONSTS.routes.pageNotFound,
    }
  }

  private static getDefaultRouteName(featureNodes: FeatureNode[]): string | undefined {
    for (const featureNode of featureNodes) {
      if (featureNode.permissionLevel !== PermissionLevelEnum.NONE) {
        if (featureNode.children) {
          const defaultRouteName = AuthorizeRouteGuard.getDefaultRouteName(featureNode.children)
          if (defaultRouteName) {
            return defaultRouteName
          }
        }

        if (featureNode.routeName) {
          return featureNode.routeName
        }
      }
    }
  }

  private static getContext(): ContextState {
    return CONTAINER.get<ContextProvider>(TYPES.ContextProvider)()
  }
}
